import { Box, Button, Flex, useToast, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { funding as commonFunding } from '@finley-technologies/common';

import {
  getUnexpectedErrorMessage,
  numberWithCommas,
} from 'packages/common-frontend/utils';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { useSubmitFundingRequestMutation } from '~/borrower/domains/fundingRequests/requests/fundingRequests.gql.generated';
import { useFundingRequestContext } from '~/borrower/domains/fundingRequests/ui/manage/Context';
import {
  refreshFundingRequestWithFullDetails,
  updateTargetFundingDate,
} from '~/borrower/domains/fundingRequests/ui/manage/Reducer';
import {
  FundingRequestComponentsTable,
  TargetFundingDateEditFunction,
} from '~/borrower/domains/fundingRequests/ui/shared/ComponentsTable';
import { FundingRequestSideColumn } from '~/borrower/domains/fundingRequests/ui/shared/ContainerSections';
import { FundingRequestInfoBox } from '~/borrower/domains/fundingRequests/ui/shared/InfoBox';
import { GetEventsDocumentGQLType } from '~/core/routes/events/legacy/events.gql.generated';
import { PageHeader } from '~/packages/common-frontend/components/PageContainer';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';
import {
  GetFundingRequestsWithBaseDetailsDocumentGQLType,
  useGetMaximumDrawRequestFrequencyMetadataQuery,
} from '~/packages/funding-requests/requests/requests.gql.generated';

const FundingRequestReviewMain = () => {
  const toast = useToast();
  const { isLoading, state, dispatch } = useFundingRequestContext();
  const {
    bankAccountMap,
    components,
    componentDefaultTargetFundingDates,
    constraints,
    creditAgreementId,
    creditFacilityId,
    componentTotalAmounts: {
      collectionsRecycleTotalAmount,
      drawRequestTotalAmount,
    },
    _cashAdvanceRate,
    _adjustedPrincipalBalance,
    _totalMaxAdvanceAmount,
  } = state;
  const {
    isCollectionsRecycleEnabled,
    isVoluntaryPaydownAllowed,
    maxFundingRequestAmount,
  } = constraints._masterConstraints;

  const {
    loading: getMaximumDrawRequestFrequencyMetadataQueryLoading,
    data: getMaximumDrawRequestFrequencyMetadataQueryData,
  } = useGetMaximumDrawRequestFrequencyMetadataQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    // skip the initial query which occurs when state === fundingRequestInitialState (meaning the default
    // creditFacilityId or creditAgreementId of an empty string would be used)
    skip: !creditFacilityId || !creditAgreementId,
    variables: {
      creditAgreementId,
      creditFacilityId,
    },
  });

  const targetFundingDateEditFunction: TargetFundingDateEditFunction = (
    id,
    type,
    fullDateStr,
  ) => dispatch(updateTargetFundingDate(id, type, fullDateStr));

  if (
    isLoading ||
    getMaximumDrawRequestFrequencyMetadataQueryLoading ||
    !getMaximumDrawRequestFrequencyMetadataQueryData
  ) {
    return <></>;
  }

  const {
    limitReached: drawRequestFrequencyMetadataLimitReached,
    message: drawRequestFrequencyMetadataMessage,
  } = getMaximumDrawRequestFrequencyMetadataQueryData.maximumDrawRequestFrequencyMetadata;

  const drawRequestFrequencyMessage =
    drawRequestFrequencyMetadataLimitReached &&
    drawRequestTotalAmount > 0 &&
    drawRequestFrequencyMetadataMessage
      ? drawRequestFrequencyMetadataMessage
      : undefined;

  // Calculation also reflected in Plan.tsx component and funding-request controller generateFundingRequestNotificationsBaseParams
  // This equation becomes invalid if we allow simultaneous collections paydown and draw request
  const prefundedDrawContribution = commonFunding.getPrefundedDrawContribution({
    cashAdvanceRate: _cashAdvanceRate,
    collectionsRecycleTotalAmount,
    currentPrincipalBalance: _adjustedPrincipalBalance,
    drawRequestTotalAmount,
    totalMaxAdvanceAmount: _totalMaxAdvanceAmount,
  });

  let prefundedDrawContributionMessage;
  if (prefundedDrawContribution > 0) {
    const formattedPrefundedDrawContribution =
      numberWithCommas(prefundedDrawContribution) || 0;
    prefundedDrawContributionMessage = `(includes prefunded draw\nof $${formattedPrefundedDrawContribution})`;
  }

  return (
    <VStack spacing={4} alignItems="left">
      <PageHeader
        headerLabel="Confirm funding request"
        subheaderLabel="Please review and confirm the funding details below."
      />
      <Box borderWidth="1px">
        <FundingRequestComponentsTable
          bankAccountMap={bankAccountMap}
          fundingRequestComponents={components}
          isCollectionsRecycleEnabled={isCollectionsRecycleEnabled}
          isVoluntaryPaydownAllowed={isVoluntaryPaydownAllowed}
          maxFundingRequestAmount={maxFundingRequestAmount}
          defaultTargetFundingDates={componentDefaultTargetFundingDates}
          onTargetFundingDateEdit={targetFundingDateEditFunction}
          drawRequestFrequencyMessage={drawRequestFrequencyMessage}
          prefundedDrawContributionMessage={prefundedDrawContributionMessage}
        />
      </Box>
    </VStack>
  );
};

export const FundingRequestReviewViewBody = ({
  toPlanTab,
  toMonitorTab,
}: {
  toPlanTab: () => void;
  toMonitorTab: () => void;
}) => {
  const { fundingRequestId, state, dispatch } = useFundingRequestContext();
  const { creditFacilityId } = useCreditFacilityContext();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitFundingRequestMutate] = useSubmitFundingRequestMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      // reverted to toast on all errors for this mutation because customer uses toast as CTA for errors
      errorToastConfig.title =
        err.message ||
        'An unexpected error occurred. Please contact Finley for support.';
      toast(errorToastConfig);
    },
    refetchQueries: [
      {
        query: GetFundingRequestsWithBaseDetailsDocumentGQLType,
        variables: { creditFacilityIds: [creditFacilityId] },
      },
      GetEventsDocumentGQLType,
    ],
  });

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      // construct fundingRequestComponentChangesets from state
      const { components } = state;

      const { data } = await submitFundingRequestMutate({
        variables: {
          fundingRequestChangeset: {
            id: fundingRequestId,
            fundingRequestComponents: _.flatten(_.values(components)),
          },
        },
      });
      if (data && data.submitFundingRequest) {
        // TODO:Tosh, One submit is completed and successful, we need to update state and redirect to `monitor` tab
        // It's a bit dirty... For this, tab control should be invoked from change in data, but we need to invoke
        // reducer -> tab control seems also very strange
        successToastConfig.title = 'Submitted funding request successfully';
        toast(successToastConfig);
        dispatch(
          refreshFundingRequestWithFullDetails(data.submitFundingRequest),
        );
        setIsSubmitting(false);
        toMonitorTab();
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error submitting funding request`;
        toast(errorToastConfig);
        setIsSubmitting(false);
      }
      throw err;
    }
  }, [
    fundingRequestId,
    submitFundingRequestMutate,
    state,
    dispatch,
    toast,
    toMonitorTab,
  ]);

  // NOTE: It would be nice, if we can put Right display box under FundingRequestViewBody
  //  {/*<Flex alignItems="center" justify="flex-start" width="auto" flexGrow={1}> */}
  //
  //  TODO: This Formik can also be "passed" using context, so it the logic can live close to store and reducer
  const ButtonRow = (
    <>
      <Button
        isDisabled={isSubmitting}
        flexBasis="50%"
        onClick={toPlanTab}
        leftIcon={<FiArrowLeft />}
        size="lg"
        borderRadius="0"
      >
        Modify
      </Button>
      <Button
        isLoading={isSubmitting}
        marginLeft="22px"
        flexBasis="50%"
        type="submit"
        rightIcon={<FiArrowRight />}
        backgroundColor="#3DCC91"
        color="white"
        _hover={{ bg: '#0F9960' }}
        size="lg"
        borderRadius="0"
      >
        Confirm
      </Button>
    </>
  );
  return (
    <Formik initialValues={{}} onSubmit={handleSubmit}>
      <Form data-testid="settings-user-edit-form">
        <Flex justify="flex-start" width="auto" flexGrow={1}>
          <Box m={2} flex="5">
            <FundingRequestReviewMain />
          </Box>
          <FundingRequestSideColumn
            ButtonRow={ButtonRow}
            FundingRequestInfoBox={<FundingRequestInfoBox />}
          />
        </Flex>
      </Form>
    </Formik>
  );
};
